import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
} from "../../../Common/__styles__/Modal";
import { Button } from "../../../Common/Buttons/Button";
import Modal from "../../../Common/Modal";
import { Text } from "../../../Inputs/react-hook-form";

type SectionTitleModalProps = {
  hide: () => void;
  onSave: (title: string) => void;
  initialTitle?: string;
  modalTitle: string;
  buttonLabel: string;
};

interface SectionTitleFormStructure {
  title: string;
}

const SectionTitleModal = ({
  hide,
  onSave,
  initialTitle,
  modalTitle,
  buttonLabel,
}: SectionTitleModalProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SectionTitleFormStructure>({
    defaultValues: {
      title: initialTitle || "",
    },
  });

  const handleSave = async ({ title }: SectionTitleFormStructure) => {
    onSave(title);
    hide();
  };

  return (
    <Container width="narrow">
      <HeaderSection>
        <h1>{modalTitle}</h1>
        <h2>Homepage highlights</h2>
      </HeaderSection>
      <FormSection
        onSubmit={handleSubmit(async userProvidedData => {
          await handleSave({
            title: userProvidedData.title,
          });
        })}
      >
        <ContentSection data-testid="inputs">
          <Text
            label="Section title"
            error={errors.title?.message}
            {...register("title", {
              required: "Section title is required",
            })}
            required
          />
        </ContentSection>
        <ButtonSection>
          <PrimaryButtons>
            <Button
              variant="secondary"
              type="button"
              onPress={hide}
              size="medium"
              title="Cancel"
            />
            <Button
              variant="primary"
              type="submit"
              isDisabled={Object.keys(errors).length > 0}
              size="medium"
              title={buttonLabel}
            />
          </PrimaryButtons>
        </ButtonSection>
      </FormSection>
    </Container>
  );
};

type AddSectionModalHookProps = {
  onAdd: (title: string) => void;
};

export const useAddSectionModal = ({ onAdd }: AddSectionModalHookProps) => {
  const [show, hide] = useModal(
    () => (
      <Modal onRequestClose={() => hide()}>
        <SectionTitleModal
          onSave={onAdd}
          hide={hide}
          modalTitle="Add a section"
          buttonLabel="Add"
        />
      </Modal>
    ),
    [onAdd],
  );

  return [show, hide] as const;
};

type EditSectionModalHookProps = {
  onUpdate: (title: string) => void;
  initialTitle: string;
};

export const useEditSectionModal = ({
  onUpdate,
  initialTitle,
}: EditSectionModalHookProps) => {
  const [show, hide] = useModal(
    () => (
      <Modal onRequestClose={() => hide()}>
        <SectionTitleModal
          onSave={onUpdate}
          hide={hide}
          initialTitle={initialTitle}
          modalTitle="Edit section"
          buttonLabel="Save"
        />
      </Modal>
    ),
    [onUpdate, initialTitle],
  );

  return [show, hide] as const;
};
